import { pure } from '@core/helpers/pure';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  TuiDialogService,
  TuiDataList,
  TuiError,
  TuiButton,
  TuiInput,
  TuiDropdown,
  TuiTextfield,
} from '@taiga-ui/core';
import {
  TuiDataListWrapper,
  TUI_CONFIRM,
  TuiSelect,
  TuiChevron,
} from '@taiga-ui/kit';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import {
  BehaviorSubject,
  EMPTY,
  filter,
  Observable,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestDTO } from '@core/types/Request';
import { XAlertService } from '@core/services/x-alert.service';
import { NgClass } from '@angular/common';
import { RequestService } from '@core/services/request/request.service';
import { IsLoadingService } from '@core/services/is-loading.service';
import { RequestDevicesService } from '@core/services/request/request-devices.service';
import {
  RequestFormOBDTranslations,
  RequestFormOBDTranslationsPayload,
  RequestObdStatus,
  RequestOBDStatusesTranslationsPayload,
  RequestOBDSuccessReadTranslationsPayload,
} from '@core/types';
import { TranslatePipe } from '@ngx-translate/core';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import {
  REQUEST_OBD_STATUSES_TRANSLATION_KEY,
  REQUEST_OBD_SUCCESS_READ_TRANSLATION_KEY,
} from '@core/constants';

enum FormFields {
  ObdStatus = 'obdStatus',
  ObdReadSuccess = 'obdReadSuccess',
  ObdComment = 'obdComment',
}

type FormType = Pick<RequestDTO, 'obdStatus' | 'obdReadSuccess' | 'obdComment'>;

const obdStatusesOptions = [
  { id: RequestObdStatus.HasOBD2, name: 'Есть разъем OBD2' },
  {
    id: RequestObdStatus.HasNoAccessToSocket,
    name: 'Нет доступа / возможности поиска разъема',
  },
  {
    id: RequestObdStatus.HasSocketButNotOBD2,
    name: 'Есть диагностический разъем, но не OBD2',
  },
  { id: RequestObdStatus.HasNoSocket, name: 'Нет диагностического разъема' },
];

const obdReadSuccessOptions = [
  {
    id: 1,
    name: 'Считано',
    value: true,
  },
  {
    id: 2,
    name: 'Не считано',
    value: false,
  },
];

@Component({
  selector: 'x-request-form-common-obd',
  imports: [
    TuiTextfield,
    TuiChevron,
    ReactiveFormsModule,
    TuiInput,
    TuiSelect,
    TuiDataList,
    TuiDataListWrapper,
    TuiError,
    TuiButton,
    TranslatePipe,
    NgClass,
    TuiDropdown,
  ],
  templateUrl: './request-form-obd.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormObdComponent implements OnInit {
  $isInsideSummary = input<boolean>(false, {
    alias: 'isInsideSummary',
  });
  private readonly formBuilder = inject(FormBuilder);
  private readonly requestService = inject(RequestService);
  private readonly requestDevicesService = inject(RequestDevicesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService$ = inject(TuiDialogService);
  private readonly xAlertService = inject(XAlertService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);

  translates: RequestOBDStatusesTranslationsPayload &
    RequestOBDSuccessReadTranslationsPayload &
    RequestFormOBDTranslationsPayload = {
    [REQUEST_OBD_STATUSES_TRANSLATION_KEY]: {
      '1': 'Есть разъем OBD2',
      '2': 'Нет доступа / возможности поиска разъема',
      '3': 'Есть диагностический разъем, но не OBD2',
      '4': 'Нет диагностического разъема',
    },
    [REQUEST_OBD_SUCCESS_READ_TRANSLATION_KEY]: {
      '1': 'Считано',
      '2': 'Не считано',
    },
    [RequestFormOBDTranslations.SuccessUpdatingOBDData]:
      'Данные OBD успешно обновлены',
    [RequestFormOBDTranslations.DialogUpdateOBDData]:
      'Вы уверены что хотите сохранить данные OBD?',
    [RequestFormOBDTranslations.DialogUpdateOBDDataYes]: 'Да',
    [RequestFormOBDTranslations.DialogUpdateOBDDataNo]: 'Нет',
  };

  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);
  readonly permissionService = inject(OutkeepPermissionService);
  readonly FormFields = FormFields;
  readonly updateRequestObdInfo$ = new Subject<void>();
  readonly obdStatusesOptions = obdStatusesOptions;
  readonly obdReadSuccessOptions = obdReadSuccessOptions;

  readonly obdStatusStringifyFn = (value: number): string => {
    const obdStatus = obdStatusesOptions.find(option => option.id === value);

    if (obdStatus) {
      return this.translates.obd_statuses[obdStatus.id];
    }

    return '';
  };

  readonly obdReadSuccessStringifyFn = (value: boolean): string => {
    const obdReadSuccess = obdReadSuccessOptions.find(
      option => option.value === value,
    );

    if (obdReadSuccess) {
      const id = obdReadSuccess.id.toString();

      return this.translates.obd_success_read[
        id as keyof RequestOBDSuccessReadTranslationsPayload['obd_success_read']
      ];
    }

    return '';
  };

  formGroup = this.formBuilder.group<FormType>({
    [FormFields.ObdStatus]: null,
    [FormFields.ObdReadSuccess]: null,
    [FormFields.ObdComment]: '',
  });

  @pure
  get obdStatusField() {
    return this.formGroup.controls[FormFields.ObdStatus];
  }

  @pure
  get obdReadSuccessField() {
    return this.formGroup.controls[FormFields.ObdReadSuccess];
  }

  @pure
  get obdCommentField() {
    return this.formGroup.controls[FormFields.ObdComment];
  }

  get isObdCommentFieldTouched(): boolean {
    return this.obdCommentField.touched ?? false;
  }

  get isObdStatusFieldTouched(): boolean {
    return this.obdStatusField.touched ?? false;
  }

  ngOnInit() {
    this.outkeepTranslateService
      .translate([
        REQUEST_OBD_STATUSES_TRANSLATION_KEY,
        REQUEST_OBD_SUCCESS_READ_TRANSLATION_KEY,
        ...Object.values(RequestFormOBDTranslations),
      ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));

    this.addFieldsValidators();
    this.formGroup.patchValue(this.requestService.$request());
    this.updateRequestObdInfo$
      .pipe(
        switchMap(() => this.performRequestObdInfoUpdate$()),
        tap(() => this.requestService.reloadRequest()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private addFieldsValidators() {
    this.obdStatusField.addValidators(Validators.required);
    this.obdCommentField.addValidators(Validators.required);
  }

  private performRequestObdInfoUpdate$(): Observable<void> {
    if (this.formGroup.invalid) {
      return EMPTY;
    }

    return this.dialogService$
      .open<boolean>(TUI_CONFIRM, {
        label: this.translates[RequestFormOBDTranslations.DialogUpdateOBDData],
        size: 's',
        closable: false,
        data: {
          yes: this.translates[
            RequestFormOBDTranslations.DialogUpdateOBDDataYes
          ],
          no: this.translates[RequestFormOBDTranslations.DialogUpdateOBDDataNo],
        },
      })
      .pipe(
        filter(Boolean),
        switchMap(() =>
          this.requestDevicesService.updateRequestObdInfo$(
            this.requestService.$request().id,
            {
              obdStatus: this.obdStatusField.value,
              obdComment: this.obdCommentField.value,
              obdReadSuccess: this.obdReadSuccessField.value,
            },
          ),
        ),
        tap(() => {
          this.xAlertService.showMessage(
            this.translates[RequestFormOBDTranslations.SuccessUpdatingOBDData],
            {
              appearance: 'positive',
            },
          );
          this.formGroup.markAsPristine();
        }),
      );
  }

  public cancel() {
    this.formGroup.reset();
  }
}
